import React, { useCallback, useEffect, useRef, useState } from "react"
import { api, ApiError } from "../../lib/api"
import { Button } from "../ui/Button"
import { Modal } from "../ui/Modal"
import { Spinner } from "../ui/Spinner"
import type { Plan, PixCharge, PurchaseStatus } from "../../types"

interface PixModalProps {
  plan: Plan
  onClose: () => void
  /** Chamado quando o webhook confirma o pagamento */
  onPaid: (purchaseId: string) => void
  /** Fallback: pagar com cartão (checkout Mercado Pago) */
  onCard?: () => void
  /**
   * Retomada de checkout: quando presente, opera sobre esta compra pendente já
   * existente (via /api/purchases/:id/{pix,checkout}) em vez de criar uma nova.
   */
  recoveryPurchaseId?: string | null
}

/**
 * Pix transparente (Asaas): CPF → QR Code na própria página → polling até o
 * webhook confirmar. A pessoa paga na hora da emoção, sem redirect.
 */
export function PixModal({ plan, onClose, onPaid, onCard, recoveryPurchaseId }: PixModalProps) {
  const [cpf, setCpf] = useState("")
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [purchaseId, setPurchaseId] = useState<string | null>(null)
  const [pix, setPix] = useState<PixCharge | null>(null)
  const [copied, setCopied] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = null
  }, [])

  useEffect(() => stopPolling, [stopPolling])

  // O passo cego: abrir o modal não gera request até o CPF. Dispara e não
  // espera — medir nunca pode segurar o fluxo de pagamento.
  useEffect(() => {
    api.post("/api/events", { name: "clique_pagar", plan: plan.slug, gateway: "pix" }).catch(() => {})
  }, [plan.slug])

  useEffect(() => {
    if (!purchaseId || !pix) return
    pollRef.current = setInterval(() => {
      api
        .get<PurchaseStatus>(`/api/purchases/${purchaseId}`)
        .then((p) => {
          if (p.status === "paid") {
            stopPolling()
            onPaid(String(purchaseId))
          }
        })
        .catch(() => {
          /* rede oscilou: o próximo tick tenta de novo */
        })
    }, 3000)
    return stopPolling
  }, [purchaseId, pix, onPaid, stopPolling])

  async function generatePix() {
    setCreating(true)
    setError(null)
    try {
      // Retomada: gera o Pix da compra pendente existente; senão, cria uma nova.
      const res = recoveryPurchaseId
        ? await api.post<{ purchase_id: string; pix?: PixCharge; init_point?: string }>(
            `/api/purchases/${recoveryPurchaseId}/pix`,
            { cpf },
          )
        : await api.post<{ purchase_id: string; pix?: PixCharge; init_point?: string }>("/api/purchases", {
            plan_id: plan.id,
            method: "pix",
            cpf,
          })
      if (!res.pix && res.init_point) {
        // Asaas não configurado (dev): cai no fluxo antigo
        window.location.href = res.init_point
        return
      }
      setPurchaseId(String(res.purchase_id))
      setPix(res.pix ?? null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Não foi possível gerar o Pix. Tente de novo.")
    } finally {
      setCreating(false)
    }
  }

  // Cartão: na retomada, gera a preference da compra existente; senão delega ao pai.
  async function payWithCard() {
    api.post("/api/events", { name: "clique_pagar", plan: plan.slug, gateway: "mercado_pago" }).catch(() => {})
    if (!recoveryPurchaseId) {
      onCard?.()
      return
    }
    setCreating(true)
    setError(null)
    try {
      const res = await api.post<{ init_point: string }>(`/api/purchases/${recoveryPurchaseId}/checkout`, {})
      window.location.href = res.init_point
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Não foi possível abrir o cartão. Tente de novo.")
      setCreating(false)
    }
  }

  async function copyPayload() {
    if (!pix) return
    try {
      await navigator.clipboard.writeText(pix.qr_payload)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      /* clipboard bloqueado: usuário pode selecionar manualmente */
    }
  }

  const cpfDigits = cpf.replace(/\D/g, "")

  return (
    <Modal open onClose={onClose} title={`Pagar com Pix · Plano ${plan.name}`}>
      <div>
        <p className="text-sm font-semibold text-navy-800">
          {plan.duration_days} dias de acesso · {(plan.price_cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
        </p>

        {!pix ? (
          <>
            <label htmlFor="pix-cpf" className="mt-4 block text-sm font-medium text-navy-700">
              Seu CPF <span className="text-navy-400">(exigido pelo banco pra emitir o Pix)</span>
            </label>
            <input
              id="pix-cpf"
              inputMode="numeric"
              autoComplete="off"
              placeholder="000.000.000-00"
              value={cpf}
              onChange={(e) => setCpf(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-slate-300 px-4 py-3 text-base tabular-nums focus:border-navy-600 focus:outline-none"
            />
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            <Button
              block
              className="mt-4"
              onClick={() => void generatePix()}
              disabled={creating || cpfDigits.length !== 11}
            >
              {creating ? <Spinner className="h-4 w-4 text-white" /> : "Gerar QR Code Pix"}
            </Button>
          </>
        ) : (
          <>
            <div className="mt-4 flex flex-col items-center">
              <img
                src={`data:image/png;base64,${pix.qr_image_base64}`}
                alt="QR Code Pix"
                className="h-52 w-52 rounded-xl border border-slate-200"
              />
              <p className="mt-3 flex items-center gap-2 text-sm text-navy-600">
                <Spinner className="h-4 w-4 text-navy-400" />
                Aguardando pagamento — confirma sozinho aqui
              </p>
            </div>
            <Button block variant="secondary" className="mt-4" onClick={() => void copyPayload()}>
              {copied ? "Código copiado ✓" : "Copiar código Pix (copia e cola)"}
            </Button>
            <p className="mt-3 text-center text-xs text-navy-400">
              Abra o app do seu banco, escaneie o QR ou cole o código. O acesso libera na hora do pagamento.
            </p>
          </>
        )}

        {(onCard || recoveryPurchaseId) && !pix && (
          <button
            type="button"
            onClick={() => void payWithCard()}
            disabled={creating}
            className="mt-3 w-full text-center text-sm font-medium text-navy-500 underline-offset-2 hover:underline disabled:opacity-50"
          >
            Prefere pagar com cartão?
          </button>
        )}
      </div>
    </Modal>
  )
}
